import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, ViewChild } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { NguCarouselConfig } from '@ngu/carousel';
import { Mensaje } from '@mensaje';
import { ServicioCarruselV3Service } from '../carrusel-imagenes-v3/servicio-carrusel-v3.service';

/**
 * Configuración de una instancia del carrusel.
 * @author Jherson Alejandro Rendón Rivera
 * @date 2026-08-01
 */
export interface ConfiguracionCarruselV4 {
  /** Título que se muestra sobre el carrusel. */
  titulo?: string;
  /** Ancho bootstrap del título (por defecto col-md-8). */
  tamanoTitulo?: string;
  /** Ancho bootstrap de la botonera (por defecto col-md-4). */
  tamanoBoton?: string;
  /** true = solo lectura, oculta los botones de adicionar/eliminar. */
  consultar?: boolean;
  /** Tope de imágenes permitidas. 0 o undefined = sin tope. */
  maxImagenes?: number;
  /**
   * Id de SigmaConsulta que resuelve la RUTA BASE del FTP.
   * Todas las `SUBIR_IMAGENES_*` comparten el mismo `sco_json` (consultan
   * `FRA_S_TRA_BASE_FUNARCHIVO` por sistema); lo que cambia es que **este id es la
   * llave del mapa `rutas`** dentro de esa configuración. Ej.:
   * `SUBIR_IMAGENES_CARACTERIZACION_PUNTO` -> `adjuntos/agua/fotos/`.
   * Sin la entrada en `rutas`, la subida no tiene destino.
   */
  sigmaConsultaRuta: string;
  /**
   * Subcarpeta bajo la ruta base, sin barra final ni inicial.
   * El código del registro se agrega después: `<carpeta>/<codigo>/`.
   */
  carpeta?: string;
  /** URL del RealizarAccion del módulo dueño del registro. */
  urlServicio: string;
  /** Valor de `nombreObjeto` que espera el switch del controlador. */
  nombreObjeto: string;
  /** Acción que persiste la imagen recién subida. */
  accionAdicionar: string;
  /** Acción que borra la imagen seleccionada. */
  accionEliminar: string;
}

/**
 * Carrusel de imágenes reutilizable, seguro para usar **varias veces en el mismo formulario**.
 *
 * Se diferencia de `carrusel-imagenes-v3` en dos puntos que impedían tener dos instancias
 * en una misma pantalla:
 *  - el disparo del `input[type=file]` es por `ViewChild` y no por `document.getElementById`
 *    (la v3 usa el id fijo `fileImagen`, que se duplica con dos instancias);
 *  - el resultado se devuelve por `@Output()` y no por el servicio singleton
 *    `ServicioModuloGeneralService`, que solo admite un `hijo` registrado a la vez.
 *
 * No reemplaza a la v3: las pantallas existentes siguen usándola sin cambios.
 *
 * @author Jherson Alejandro Rendón Rivera
 * @date 2026-08-01
 */
@Component({
  selector: 'app-carrusel-imagenes-v4',
  templateUrl: './carrusel-imagenes-v4.component.html',
  styleUrls: ['./carrusel-imagenes-v4.component.scss']
})
export class CarruselImagenesV4Component implements OnInit, OnChanges {

  private static readonly EXTENSIONES_VALIDAS = ['png', 'jpg', 'jpeg'];

  /** Configuración de esta instancia. */
  @Input() configuracion: ConfiguracionCarruselV4;

  /** Arreglo persistido de imágenes, con la forma `[{ imagen: '<url>' }]`. */
  @Input() imagenes: any[] = [];

  /** Registro dueño de las imágenes; se envía completo al backend. */
  @Input() modelo: any;

  /** Código del registro dueño; se usa como subcarpeta dentro del FTP. */
  @Input() codigo: string;

  /** false mientras no haya un registro seleccionado. */
  @Input() habilitado: boolean = true;

  /** Emite el arreglo actualizado tras adicionar o eliminar. */
  @Output() imagenesActualizadas = new EventEmitter<any[]>();

  @ViewChild('inputArchivo', { static: false }) inputArchivo: ElementRef<HTMLInputElement>;

  configsCarrusel: NguCarouselConfig;
  /**
   * Cada entrada trae la ruta tal como está en BD (`almacenada`, relativa desde
   * `/herramientas`) y la URL absoluta para pintar (`visible`). El backend guarda
   * relativo porque la URL que devuelve el FTP apunta al host interno y no resuelve
   * desde el navegador; el prefijo público sale de `webConfig.urlFtp`.
   */
  rutasImagenes: Array<{ almacenada: string; visible: string }> = [];
  imagenSeleccionadaActual: { almacenada: string; visible: string };
  mensaje: Mensaje;

  constructor(private servicio: ServicioCarruselV3Service) { }

  ngOnInit(): void {
    this.mensaje = new Mensaje('', '', '');
    this.configsCarrusel = {
      grid: { xs: 3, sm: 3, md: 3, lg: 3, all: 0 },
      slide: 3,
      point: { visible: false },
      load: 3,
      touch: true,
      loop: true,
      custom: 'banner'
    };
    this.aplicarValoresPorDefecto();
    this.refrescarRutas();
  }

  ngOnChanges(): void {
    this.refrescarRutas();
  }

  /** Cupo restante; `null` cuando la configuración no define tope. */
  get cupoDisponible(): number | null {
    const max = this.configuracion && this.configuracion.maxImagenes;
    return max ? max - this.rutasImagenes.length : null;
  }

  get puedeAdicionar(): boolean {
    return this.habilitado && (this.cupoDisponible === null || this.cupoDisponible > 0);
  }

  imagenSeleccionada(entrada: { almacenada: string; visible: string }): void {
    this.imagenSeleccionadaActual = entrada;
  }

  clicBotonAdicionarImagen(): void {
    if (!this.habilitado) {
      this.mensaje.retornarMensaje('error', 'Error', 'Debe seleccionar un registro de la lista', '');
      return;
    }
    if (this.cupoDisponible !== null && this.cupoDisponible <= 0) {
      this.mensaje.retornarMensaje(
        'error',
        'Límite alcanzado',
        'Solo se permiten ' + this.configuracion.maxImagenes + ' imágenes en esta sección',
        ''
      );
      return;
    }
    this.inputArchivo.nativeElement.click();
  }

  cambioArchivo(archivos: FileList): void {
    const archivo = archivos.item(0);
    if (!archivo) {
      return;
    }
    const extension = this.obtenerExtension(archivo.name).toLowerCase();
    if (CarruselImagenesV4Component.EXTENSIONES_VALIDAS.indexOf(extension) === -1) {
      this.mensaje.retornarMensaje('error', 'Formato de imagen incorrecto', 'Los formatos permitidos son jpg, jpeg y png', '');
    } else {
      this.subirArchivo(archivo);
    }
    this.inputArchivo.nativeElement.value = '';
  }

  eliminarImagen(): void {
    if (!this.imagenSeleccionadaActual) {
      this.mensaje.retornarMensaje('error', 'Error', 'Debe seleccionar una imagen para eliminar', '');
      return;
    }
    this.mensaje.retornarMensajeFuncion(
      'confirmacion',
      '¿Desea Eliminar la Imagen?',
      '',
      '',
      this,
      'eliminarImagenConfirmacion',
      { contenedor: this }
    );
  }

  /** Invocada por `Mensaje` al confirmar el modal; por eso recibe el contenedor. */
  eliminarImagenConfirmacion(parametros): void {
    const componente: CarruselImagenesV4Component = parametros.contenedor;
    componente.mensaje.retornarMensajeFuncion('loading', 'Eliminando imagen', '', '', null, null, null);

    // Se manda la ruta ALMACENADA, no la visible: es la que el backend tiene que casar.
    const almacenada = componente.imagenSeleccionadaActual.almacenada;
    const datos = Object.assign({}, componente.modelo, {
      rutaEliminar: almacenada,
      nombreEliminar: componente.obtenerNombreArchivo(almacenada)
    });

    componente.servicio.getServicioRealizarAccion(
      '',
      componente.configuracion.nombreObjeto,
      JSON.stringify(datos),
      '',
      componente.configuracion.accionEliminar,
      componente.configuracion.urlServicio,
      ''
    ).subscribe(
      (res) => {
        componente.mensaje.cerrarMensajeCarga();
        if (res.respuesta === 'true') {
          componente.imagenSeleccionadaActual = undefined;
          componente.publicarImagenes(res.sql);
          componente.mensaje.retornarMensaje('exito', 'Correcto', 'La imagen se ha eliminado correctamente', '');
        } else {
          componente.mensaje.retornarMensaje('error', 'Error', 'Ocurrió un error al eliminar la imagen', '');
        }
      },
      (err: HttpErrorResponse) => {
        componente.mensaje.cerrarMensajeCarga();
        console.log(err);
        componente.mensaje.retornarMensaje('error', 'Error', 'Ocurrió un error al eliminar la imagen', '');
      }
    );
  }

  /** Sube el archivo al FTP y, si queda, persiste la ruta en el registro. */
  private subirArchivo(archivo: File): void {
    const nombreArchivo = this.sanearNombreArchivo(archivo.name);

    const fd = new FormData();
    fd.append('sistema', '');
    fd.append('usuario', '');
    fd.append('id', this.configuracion.sigmaConsultaRuta);
    fd.append('directorio', this.obtenerDirectorio());
    fd.append('nombreArchivo', nombreArchivo);
    fd.append('file', archivo);

    this.mensaje.retornarMensajeFuncion('loading', 'Subiendo archivo', nombreArchivo, '', null, null, null);

    this.servicio.subirArchivo(fd).subscribe(
      (res) => {
        if (res['respuesta'] === 'true') {
          this.persistirImagen(res['datos']);
        } else {
          this.mensaje.cerrarMensajeCarga();
          this.mensaje.retornarMensaje('error', 'Error', res['noticia'], '');
        }
      },
      (err: HttpErrorResponse) => {
        this.mensaje.cerrarMensajeCarga();
        console.log(err);
        this.mensaje.retornarMensaje('error', 'Error', 'Ocurrió un error al subir el archivo', '');
      }
    );
  }

  /** Manda la ruta recién subida al backend para que la agregue al arreglo del registro. */
  private persistirImagen(rutaImagen: string): void {
    const datos = Object.assign({}, this.modelo, { foto: rutaImagen });

    this.servicio.getServicioRealizarAccion(
      '',
      this.configuracion.nombreObjeto,
      JSON.stringify(datos),
      '',
      this.configuracion.accionAdicionar,
      this.configuracion.urlServicio,
      ''
    ).subscribe(
      (res) => {
        this.mensaje.cerrarMensajeCarga();
        if (res.respuesta === 'true') {
          this.publicarImagenes(res.sql);
          this.mensaje.retornarMensaje('exito', 'Operación exitosa', 'Éxito al subir el archivo', '');
        } else {
          this.mensaje.retornarMensaje('error', 'Error', 'No fue posible subir el archivo, por favor contáctese con el área de soporte.', '');
        }
      },
      (err: HttpErrorResponse) => {
        this.mensaje.cerrarMensajeCarga();
        console.log(err);
        this.mensaje.retornarMensaje('error', 'Error', 'No fue posible subir el archivo, por favor contáctese con el área de soporte.', '');
      }
    );
  }

  /** Normaliza la respuesta del backend, repinta y avisa al formulario padre. */
  private publicarImagenes(respuestaSql: any): void {
    const coleccion = this.normalizarColeccion(respuestaSql);
    this.imagenes = coleccion;
    this.refrescarRutas();
    this.imagenesActualizadas.emit(coleccion);
  }

  /** `sql` llega como arreglo o como cadena JSON según el modelo del backend. */
  private normalizarColeccion(valor: any): any[] {
    if (!valor) {
      return [];
    }
    if (Array.isArray(valor)) {
      return valor;
    }
    try {
      const convertido = JSON.parse(valor);
      return Array.isArray(convertido) ? convertido : [];
    } catch (e) {
      console.log('No fue posible interpretar el listado de imágenes: ' + e);
      return [];
    }
  }

  private refrescarRutas(): void {
    const base = this.obtenerUrlBase();
    this.rutasImagenes = this.normalizarColeccion(this.imagenes)
      .map((entrada) => (entrada && entrada.imagen ? entrada.imagen : entrada))
      .filter((ruta) => !!ruta)
      .map((ruta: string) => ({
        almacenada: ruta,
        visible: /^https?:\/\//i.test(ruta) ? ruta : base + ruta
      }));
  }

  /**
   * Prefijo público para pintar las imágenes. En BD la ruta se guarda relativa
   * (desde `/herramientas`) porque la que devuelve el FTP apunta al host interno.
   */
  private obtenerUrlBase(): string {
    try {
      const config = JSON.parse(sessionStorage.getItem('webConfig'));
      return (config && config['urlFtp']) || '';
    } catch (e) {
      console.log('No fue posible leer webConfig.urlFtp: ' + e);
      return '';
    }
  }

  private aplicarValoresPorDefecto(): void {
    this.configuracion = this.configuracion || ({} as ConfiguracionCarruselV4);
    this.configuracion.titulo = this.configuracion.titulo || 'Gestión Imagen';
    this.configuracion.tamanoTitulo = this.configuracion.tamanoTitulo || 'col-md-8';
    this.configuracion.tamanoBoton = this.configuracion.tamanoBoton || 'col-md-4';
    this.configuracion.consultar = this.configuracion.consultar || false;
  }

  /**
   * Subcarpeta destino bajo la ruta base del FTP.
   * La v3 manda solo el código del registro; acá se antepone `carpeta` cuando está
   * configurada, para no mezclar en un mismo nivel las imágenes de módulos distintos.
   */
  private obtenerDirectorio(): string {
    const carpeta = this.configuracion.carpeta ? this.configuracion.carpeta + '/' : '';
    return carpeta + (this.codigo || '') + '/';
  }

  /**
   * Deja el nombre del archivo apto para viajar dentro de una URL.
   *
   * El servicio de FTP guarda el nombre tal como se le manda, y de ahí sale la ruta
   * que termina en el informe. Ese informe lo post-procesa `gam-docimage`, que busca
   * las URLs con el patrón `[\w\d\-_./% ]+?\.(jpg|jpeg|png|gif|bmp)`: un nombre con
   * paréntesis, tildes o símbolos **no matchea y la foto desaparece del documento sin
   * ningún error**. Un espacio literal tampoco es válido en una URL HTTP.
   *
   * Ej.: `peakpx (1).jpg` -> `peakpx_1.jpg`
   *
   * @param nombre Nombre original del archivo elegido por el usuario.
   * @returns El mismo nombre con solo letras, dígitos, punto, guion y guion bajo.
   * @author Jherson Alejandro Rendón Rivera
   * @date 2026-08-01
   */
  private sanearNombreArchivo(nombre: string): string {
    const extension = this.obtenerExtension(nombre).toLowerCase();
    const base = nombre.slice(0, nombre.length - extension.length - 1);

    // Descompone los acentos para poder quitarlos (á -> a + tilde combinante).
    const sinAcentos = typeof base.normalize === 'function'
      ? base.normalize('NFD').replace(/[̀-ͯ]/g, '')
      : base;

    const limpio = sinAcentos
      .replace(/[^A-Za-z0-9._-]+/g, '_')    // todo lo demás pasa a guion bajo
      .replace(/_+/g, '_')                  // sin repeticiones
      .replace(/^[._-]+|[._-]+$/g, '');     // sin separadores sueltos en los bordes

    return (limpio || 'imagen') + '.' + extension;
  }

  private obtenerNombreArchivo(ruta: string): string {
    const posiciones = ruta.split('/');
    return posiciones[posiciones.length - 1];
  }

  private obtenerExtension(nombreArchivo: string): string {
    return nombreArchivo.slice((nombreArchivo.lastIndexOf('.') - 1 >>> 0) + 2);
  }
}
